<template>
  <div ref="scrollWrap" class="pdf-scroll-viewer" @scroll.passive="handleScroll">
    <div ref="canvasContainer" class="pdf-scroll-viewer__pages"></div>
    <div v-if="pdfLoading" class="pdf-scroll-viewer__mask">
      <i class="el-icon-loading"></i>
      <span>PDF 加载中，请稍候...</span>
    </div>
    <div v-else-if="renderError" class="pdf-scroll-viewer__mask error">
      <i class="el-icon-warning-outline"></i>
      <span>{{ renderError }}</span>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL}pdf.worker.min.js`

export default {
  name: 'PdfScrollViewer',
  props: {
    pdfUrl: {
      type: String,
      default: ''
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pdfLoading: false,
      renderError: '',
      reachedBottom: false,
      pdfDocument: null,
      renderToken: 0,
      resizeTimer: null
    }
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val && this.pdfUrl) {
          this.$nextTick(() => {
            this.loadPdf()
          })
          return
        }
        this.resetState()
      }
    },
    pdfUrl(val, oldVal) {
      if (val === oldVal) {
        return
      }
      this.reachedBottom = false
      this.emitStatus()
      if (this.active && val) {
        this.$nextTick(() => {
          this.loadPdf()
        })
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.resetState()
  },
  methods: {
    emitStatus() {
      this.$emit('status-change', {
        loading: this.pdfLoading,
        error: this.renderError,
        ready: !this.pdfLoading && !this.renderError && !!this.pdfUrl,
        reachedBottom: this.reachedBottom
      })
    },
    async loadPdf() {
      if (!this.pdfUrl || !this.active) {
        this.resetState()
        return
      }
      const currentToken = Date.now()
      this.renderToken = currentToken
      this.pdfLoading = true
      this.renderError = ''
      this.reachedBottom = false
      this.emitStatus()
      try {
        this.clearCanvas()
        if (this.pdfDocument && this.pdfDocument.destroy) {
          await this.pdfDocument.destroy()
        }
        const loadingTask = pdfjsLib.getDocument({
          url: this.pdfUrl,
          useWorkerFetch: false,
          isEvalSupported: false
        })
        const pdfDocument = await loadingTask.promise
        if (this.renderToken !== currentToken) {
          if (pdfDocument && pdfDocument.destroy) {
            await pdfDocument.destroy()
          }
          return
        }
        this.pdfDocument = pdfDocument
        await this.renderPages(pdfDocument, currentToken)
      } catch (error) {
        console.error('PdfScrollViewer loadPdf error', error)
        this.renderError = 'PDF 加载失败，请关闭后重试'
      } finally {
        if (this.renderToken === currentToken) {
          this.pdfLoading = false
          this.$nextTick(() => {
            this.checkReachedBottom()
            this.emitStatus()
          })
        }
      }
    },
    async renderPages(pdfDocument, currentToken) {
      const container = this.$refs.canvasContainer
      if (!container) {
        return
      }
      this.clearCanvas()
      const renderWidth = this.getRenderWidth()
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        if (this.renderToken !== currentToken) {
          return
        }
        const page = await pdfDocument.getPage(pageNumber)
        const baseViewport = page.getViewport({ scale: 1 })
        const scale = Math.max((renderWidth - 32) / baseViewport.width, 0.6)
        const viewport = page.getViewport({ scale })
        const pageCard = document.createElement('div')
        pageCard.className = 'pdf-scroll-viewer__page'
        const pageLabel = document.createElement('div')
        pageLabel.className = 'pdf-scroll-viewer__page-index'
        pageLabel.textContent = `第 ${pageNumber} / ${pdfDocument.numPages} 页`
        const canvas = document.createElement('canvas')
        canvas.className = 'pdf-scroll-viewer__canvas'
        const context = canvas.getContext('2d', { alpha: false })
        const ratio = window.devicePixelRatio || 1
        canvas.width = Math.floor(viewport.width * ratio)
        canvas.height = Math.floor(viewport.height * ratio)
        canvas.style.width = `${Math.floor(viewport.width)}px`
        canvas.style.height = `${Math.floor(viewport.height)}px`
        pageCard.appendChild(pageLabel)
        pageCard.appendChild(canvas)
        container.appendChild(pageCard)
        await page.render({
          canvasContext: context,
          viewport,
          transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null
        }).promise
      }
    },
    getRenderWidth() {
      const wrap = this.$refs.scrollWrap
      if (!wrap) {
        return 960
      }
      return Math.max(Math.min(wrap.clientWidth - 32, 1080), 320)
    },
    handleResize() {
      if (!this.active || !this.pdfDocument || this.pdfLoading) {
        return
      }
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(() => {
        this.loadPdf()
      }, 200)
    },
    handleScroll() {
      this.checkReachedBottom()
    },
    checkReachedBottom() {
      const target = this.$refs.scrollWrap
      if (!target || this.pdfLoading || this.renderError) {
        return false
      }
      const reachedBottom = target.scrollHeight <= target.clientHeight + 8 || target.scrollTop + target.clientHeight >= target.scrollHeight - 24
      if (reachedBottom !== this.reachedBottom) {
        this.reachedBottom = reachedBottom
        this.emitStatus()
      }
      return reachedBottom
    },
    clearCanvas() {
      const container = this.$refs.canvasContainer
      if (container) {
        container.innerHTML = ''
      }
    },
    resetState() {
      this.renderToken = 0
      this.pdfLoading = false
      this.renderError = ''
      this.reachedBottom = false
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
        this.resizeTimer = null
      }
      this.clearCanvas()
      if (this.pdfDocument && this.pdfDocument.destroy) {
        try {
          this.pdfDocument.destroy()
        } catch (error) {
          console.warn('PdfScrollViewer destroy pdfDocument error', error)
        }
      }
      this.pdfDocument = null
      this.emitStatus()
    }
  }
}
</script>

<style scoped>
.pdf-scroll-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background: #111827;
}

.pdf-scroll-viewer__pages {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 18px 16px 24px;
  box-sizing: border-box;
}

.pdf-scroll-viewer__page {
  width: fit-content;
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  padding: 14px;
  box-sizing: border-box;
}

.pdf-scroll-viewer__page-index {
  margin-bottom: 10px;
  color: #6b7c93;
  font-size: 12px;
  text-align: center;
}

.pdf-scroll-viewer__canvas {
  display: block;
  max-width: 100%;
  margin: 0 auto;
  background: #fff;
}

.pdf-scroll-viewer__mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(17, 24, 39, 0.72);
  color: #fff;
  font-size: 14px;
  z-index: 2;
}

.pdf-scroll-viewer__mask.error {
  color: #ffd6d6;
}
</style>